// react
import { useMemo, useState } from 'react';
// libraries
import { useController } from 'react-hook-form';
import type { Control } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Autocomplete,
  Box,
  Chip,
  LinearProgress,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

// api
import type { GetEnergyTreeNodeDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { RHFFieldLabel } from '@/components/ui/form';

// feature-utils
import { getDublicateViolations } from '@/features/energyxplorer/utils/energyTreeFunctions';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
import {
  getTranslatedNodeType,
  getTypeColor,
  getTypeIcon,
} from '@/utils/nodeTypeMapper';

// relative
import { asEnergyTreeNodeOption } from './initiativeForm.mappers';
import type { InitiativeFormValues } from './initiativeForm.schema';

type SourceSelectionMode = 'node' | 'seu';

export default function InitiativeSourceSelector({
  control,
  nodes,
  disabled,
  onSourcesBlur,
}: {
  control: Control<InitiativeFormValues>;
  nodes: GetEnergyTreeNodeDto[];
  disabled?: boolean;
  // Fired when the node/SEU picker loses focus, or when a selection is
  // removed/cleared (which doesn't blur the input), so the parent form can
  // auto-fetch the graph with the up-to-date node ids.
  onSourcesBlur?: (nodeIds: string[]) => void;
}) {
  const { t } = useTranslation(['forms', 'validation']);
  const [sourceSelectionMode, setSourceSelectionMode] =
    useState<SourceSelectionMode>('node');

  const { field, fieldState } = useController({
    name: 'energyTreeNodes',
    control,
  });

  const nodeOptions = useMemo(
    () => nodes.map(asEnergyTreeNodeOption).filter((option) => option.id),
    [nodes],
  );

  const nodeGroupsByType = useMemo(() => {
    const map = new Map<string, GetEnergyTreeNodeDto[]>();
    nodes.forEach((node) => {
      const type = node?.type;
      if (!type) return;
      const group = map.get(type) ?? [];
      group.push(node);
      map.set(type, group);
    });
    return map;
  }, [nodes]);

  const seuTypes = useMemo(
    () => Array.from(nodeGroupsByType.keys()),
    [nodeGroupsByType],
  );

  const selectedNodeOptions = nodeOptions.filter((option) =>
    field.value.includes(option.id),
  );

  const isDuplicated =
    getDublicateViolations(
      selectedNodeOptions.map((option) => ({ id: option.id })),
      nodes,
    ).length > 0;

  useAlertProvider({
    enabled: isDuplicated,
    severity: 'warning',
    message: t('createInitiative.duplicateNodesWarning', { ns: 'forms' }),
  });

  const selectedTypes = Array.from(
    new Set(
      field.value
        .map((id) => nodes.find((node) => node.energyTreeNodeId === id))
        .filter((node): node is GetEnergyTreeNodeDto => Boolean(node))
        .map((node) => node.type)
        .filter((type): type is string => Boolean(type)),
    ),
  );

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup
        value={sourceSelectionMode}
        fullWidth
        size="small"
        exclusive
        disabled={disabled}
        onChange={(_event, newMode: SourceSelectionMode | null) => {
          if (newMode !== null) setSourceSelectionMode(newMode);
        }}
        sx={{ alignSelf: 'flex-start', mr: 1 }}
      >
        <ToggleButton value="node" data-testid="initiative-source-mode-node">
          <ListItemText
            primary={t('createInitiative.sourceMode.nodes', { ns: 'forms' })}
          />
        </ToggleButton>
        <ToggleButton value="seu" data-testid="initiative-source-mode-seu">
          <ListItemText
            primary={t('createInitiative.sourceMode.seu', { ns: 'forms' })}
          />
        </ToggleButton>
      </ToggleButtonGroup>

      {sourceSelectionMode === 'node' && (
        <Autocomplete
          multiple
          options={nodeOptions}
          size="small"
          disabled={disabled}
          value={selectedNodeOptions}
          onChange={(_event, val, reason) => {
            const ids = val.map((v) => v.id);
            field.onChange(ids);
            // Removing a chip or clearing the field doesn't blur the
            // input, so trigger the auto-fetch directly for those actions.
            if (reason === 'removeOption' || reason === 'clear') {
              onSourcesBlur?.(ids);
            }
          }}
          onBlur={() => {
            field.onBlur();
            onSourcesBlur?.(field.value);
          }}
          disableCloseOnSelect
          filterSelectedOptions
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box
                sx={{
                  backgroundColor: getTypeColor(option?.type),
                  position: 'relative',
                  borderRadius: '25px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: `1px solid divider`,
                  marginRight: 1,
                }}
              >
                <i
                  style={{ color: 'white' }}
                  className={`fa-solid ${getTypeIcon(option?.type)}`}
                />
              </Box>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <Box>
              <RHFFieldLabel htmlFor={params.id} required>
                {t('createInitiative.nodes.label', { ns: 'forms' })}
              </RHFFieldLabel>
              <TextField
                data-testid="create-initiative-node-select-input"
                {...params}
                placeholder={t('createInitiative.nodes.placeholder', {
                  ns: 'forms',
                })}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  t('createInitiative.nodes.helperText', { ns: 'forms' })
                }
              />
            </Box>
          )}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getItemProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.label}
                  {...tagProps}
                  sx={{
                    backgroundColor: getTypeColor(option.type),
                    color: 'white',
                    borderRadius: 2,
                  }}
                />
              );
            })
          }
        />
      )}

      {sourceSelectionMode === 'seu' && (
        <Autocomplete
          multiple
          options={seuTypes}
          disabled={disabled}
          getOptionLabel={(type) => getTranslatedNodeType(type, t)}
          value={selectedTypes}
          onChange={(_event, newSelectedTypes, reason) => {
            const selectedIds = newSelectedTypes
              .flatMap((type) => nodeGroupsByType.get(type) ?? [])
              .map((node) => node.energyTreeNodeId as string);
            field.onChange(selectedIds);
            // Removing a chip or clearing the field doesn't blur the
            // input, so trigger the auto-fetch directly for those actions.
            if (reason === 'removeOption' || reason === 'clear') {
              onSourcesBlur?.(selectedIds);
            }
          }}
          onBlur={() => {
            field.onBlur();
            onSourcesBlur?.(field.value);
          }}
          renderInput={(params) => (
            <Box key={params.id}>
              <RHFFieldLabel htmlFor={params.id} required>
                {t('createInitiative.sourceMode.seuLabel', {
                  ns: 'forms',
                })}
              </RHFFieldLabel>
              <TextField
                data-testid="initiative-seu-select-input"
                {...params}
                placeholder={t('createInitiative.sourceMode.seuPlaceholder', {
                  ns: 'forms',
                })}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  t('createInitiative.sourceMode.seuHelperText', {
                    ns: 'forms',
                  })
                }
              />
            </Box>
          )}
          renderOption={(props, type) => (
            <li {...props} key={type}>
              <Box
                sx={{
                  backgroundColor: getTypeColor(type),
                  position: 'relative',
                  borderRadius: '25px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: `1px solid divider`,
                  marginRight: 1,
                }}
              >
                <i
                  style={{ color: 'white' }}
                  className={`fa-solid ${getTypeIcon(type)}`}
                />
              </Box>
              <Typography sx={{ flexGrow: 1 }}>
                {getTranslatedNodeType(type, t)}
              </Typography>
            </li>
          )}
          renderValue={(value, getItemProps) =>
            value.map((type, index) => {
              const allOfType = nodeGroupsByType.get(type) ?? [];
              const selectedCount = allOfType.filter((node) =>
                field.value.includes(node.energyTreeNodeId as string),
              ).length;
              const percentage = Math.round(
                (selectedCount / allOfType.length) * 100,
              );
              const color = getTypeColor(type);
              const { key: _chipKey, ...chipProps } = getItemProps({ index });

              return (
                <Box
                  key={type}
                  sx={{ position: 'relative', display: 'inline-block', mr: 1 }}
                >
                  <Chip
                    key={_chipKey}
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <i
                          className={`fa-solid ${getTypeIcon(type)}`}
                          style={{ color: 'white' }}
                        />
                        <Typography>{type}</Typography>
                      </Box>
                    }
                    color={
                      selectedCount === allOfType.length ? 'success' : 'warning'
                    }
                    variant="filled"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: color,
                      color: 'white',
                    }}
                    {...chipProps}
                  />
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      left: 0,
                      right: 0,
                      height: 3,
                      borderRadius: 1,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          selectedCount === allOfType.length
                            ? '#2e7d32'
                            : '#ed6c02',
                      },
                    }}
                  />
                </Box>
              );
            })
          }
        />
      )}
    </Stack>
  );
}
